import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  not_started: 'bg-gray-500/20 text-gray-400 border-gray-500/50',
  planned: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50',
  in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  project_work: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
  tender_phase: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  completed: 'bg-green-500/20 text-green-400 border-green-500/50'
};

const statusLabels = {
  not_started: 'Başlanmadı',
  planned: 'Planlanan',
  in_progress: 'Devam Ediyor',
  project_work: 'Proje İşleri',
  tender_phase: 'İhale Aşamasında',
  completed: 'Tamamlandı'
};

const priorityColors = {
  low: 'bg-green-500/20 text-green-400 border-green-500/50',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  urgent: 'bg-red-500/20 text-red-400 border-red-500/50',
  very_urgent: 'bg-pink-500/20 text-pink-400 border-pink-500/50'
};

const priorityLabels = {
  low: 'Düşük',
  medium: 'Orta',
  high: 'Yüksek',
  urgent: 'Acil',
  very_urgent: 'Çok Acil'
};

const CalendarPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dayTasksDialogOpen, setDayTasksDialogOpen] = useState(false);
  const [selectedDayTasks, setSelectedDayTasks] = useState([]);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks`, {
        withCredentials: true
      });
      setAllTasks(response.data);
      filterTasksByDate(new Date(), response.data);
    } catch (error) {
      console.error('Tasks fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTasksByDate = (date, tasksList = allTasks) => {
    const dateStr = date.toISOString().split('T')[0];
    const filtered = tasksList.filter(task => {
      const startDate = new Date(task.start_date).toISOString().split('T')[0];
      const dueDate = new Date(task.due_date).toISOString().split('T')[0];
      const deadlineDate = new Date(task.deadline_date).toISOString().split('T')[0];
      
      return dateStr >= startDate && dateStr <= deadlineDate;
    });
    setTasks(filtered);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    filterTasksByDate(date);
  };

  const handleDayClick = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const dayTasks = allTasks.filter(task => {
      const startDate = new Date(task.start_date).toISOString().split('T')[0];
      const dueDate = new Date(task.due_date).toISOString().split('T')[0];
      const deadlineDate = new Date(task.deadline_date).toISOString().split('T')[0];
      
      return dateStr >= startDate && dateStr <= deadlineDate;
    });
    setSelectedDayTasks(dayTasks);
    setDayTasksDialogOpen(true);
  };

  const getTileContent = ({ date, view }) => {
    if (view === 'month') {
      const dateStr = date.toISOString().split('T')[0];
      const dayTasks = allTasks.filter(task => {
        const startDate = new Date(task.start_date).toISOString().split('T')[0];
        const deadlineDate = new Date(task.deadline_date).toISOString().split('T')[0];
        return dateStr >= startDate && dateStr <= deadlineDate;
      });

      if (dayTasks.length > 0) {
        const statusCounts = {
          not_started: 0,
          planned: 0,
          in_progress: 0,
          project_work: 0,
          tender_phase: 0,
          completed: 0
        };

        dayTasks.forEach(task => {
          statusCounts[task.status]++;
        });

        return (
          <div className="flex flex-col items-center gap-1 mt-1">
            <div className="flex gap-1">
              {statusCounts.not_started > 0 && (
                <div className="w-2 h-2 rounded-full bg-gray-500" title="Başlanmadı"></div>
              )}
              {statusCounts.planned > 0 && (
                <div className="w-2 h-2 rounded-full bg-cyan-500" title="Planlanan"></div>
              )}
              {statusCounts.in_progress > 0 && (
                <div className="w-2 h-2 rounded-full bg-blue-500" title="Devam Ediyor"></div>
              )}
              {statusCounts.project_work > 0 && (
                <div className="w-2 h-2 rounded-full bg-purple-500" title="Proje İşleri"></div>
              )}
              {statusCounts.tender_phase > 0 && (
                <div className="w-2 h-2 rounded-full bg-orange-500" title="İhale Aşamasında"></div>
              )}
              {statusCounts.completed > 0 && (
                <div className="w-2 h-2 rounded-full bg-green-500" title="Tamamlandı"></div>
              )}
            </div>
            <span className="text-xs text-gray-400">{dayTasks.length}</span>
          </div>
        );
      }
    }
    return null;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Header */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu Logo" className="w-12 h-12 object-contain" />
              <div>
                <h1 className="text-xl font-bold text-white">Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu</h1>
                <p className="text-xs text-gray-400">Yatırım ve İnşaat</p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                data-testid="anasayfa-nav-button"
              >
                Anasayfa
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Dashboard
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/tasks')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Görevler
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/calendar')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors border-b-2 border-blue-500"
              >
                Takvim
              </button>
              {(user?.is_admin || user?.is_admin_assistant) && (
                <>
                  <span className="text-gray-600">|</span>
                  <button
                    onClick={() => navigate('/admin/users')}
                    className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                  >
                    Kullanıcılar
                  </button>
                </>
              )}
              <div className="ml-4 border-l border-gray-700 pl-4 flex items-center space-x-4">
                <div 
                  className="flex items-center space-x-3 bg-gray-800/50 rounded-lg px-4 py-2 cursor-pointer hover:bg-gray-800/70 transition-colors"
                  onClick={() => navigate('/profile')}
                >
                  <img src={user?.picture} alt={user?.name} className="w-8 h-8 rounded-full" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-gray-400">
                      {user?.is_admin ? 'Admin' : user?.is_admin_assistant ? 'Admin Yardımcısı' : 'Kullanıcı'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="text-red-400 hover:text-red-300"
                >
                  Çıkış
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Takvim</h2>
          <p className="text-gray-400">Görevlerinizi tarih bazlı görüntüleyin</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Aylık Görünüm</CardTitle>
                <CardDescription className="text-gray-400">
                  Günlere tıklayarak o günün görevlerini görüntüleyin
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="calendar-wrapper">
                  <Calendar
                    onChange={handleDateChange}
                    value={selectedDate}
                    onClickDay={handleDayClick}
                    tileContent={getTileContent}
                    locale="tr-TR"
                    className="react-calendar-dark"
                  />
                </div>
                
                {/* Legend */}
                <div className="mt-6 p-4 bg-gray-800 rounded-lg">
                  <h3 className="text-sm font-medium text-white mb-3">Durum Göstergeleri</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                      <span className="text-xs text-gray-400">Başlanmadı</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                      <span className="text-xs text-gray-400">Planlanan</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="text-xs text-gray-400">Devam Ediyor</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      <span className="text-xs text-gray-400">Proje İşleri</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                      <span className="text-xs text-gray-400">İhale Aşamasında</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className="text-xs text-gray-400">Tamamlandı</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Selected Date Tasks */}
          <div>
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">
                  {selectedDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Bu tarihte devam eden görevler
                </CardDescription>
              </CardHeader>
              <CardContent>
                {tasks.length > 0 ? (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {tasks.map((task) => (
                      <div key={task.id} className="bg-gray-800 rounded-lg p-3 space-y-2">
                        <div className="flex items-start justify-between">
                          <h4 className="text-sm font-medium text-white">{task.title}</h4>
                          <Badge className={statusColors[task.status]}>
                            {statusLabels[task.status]}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-400 line-clamp-2">{task.description}</p>
                        <div className="flex items-center gap-2">
                          <Badge className={priorityColors[task.priority]} variant="outline">
                            {priorityLabels[task.priority]}
                          </Badge>
                        </div>
                        {task.assigned_to_email && (
                          <p className="text-xs text-gray-500">Atanan: {task.assigned_to_email}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">Bu tarihte devam eden görev yok</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Day Tasks Dialog */}
      <Dialog open={dayTasksDialogOpen} onOpenChange={setDayTasksDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Günün Görevleri</DialogTitle>
            <DialogDescription className="text-gray-400">
              Bu gün için tüm görevler
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedDayTasks.length > 0 ? (
              selectedDayTasks.map((task) => (
                <Card key={task.id} className="bg-gray-800 border-gray-700">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg text-white">{task.title}</CardTitle>
                      <div className="flex gap-2">
                        <Badge className={statusColors[task.status]}>
                          {statusLabels[task.status]}
                        </Badge>
                        <Badge className={priorityColors[task.priority]}>
                          {priorityLabels[task.priority]}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-gray-400 mt-2">{task.description}</p>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {task.assigned_to_email && (
                      <p className="text-sm text-gray-400">Atanan: {task.assigned_to_email}</p>
                    )}
                    <div className="text-xs text-gray-500 space-y-1">
                      <p>Başlangıç: {new Date(task.start_date).toLocaleDateString('tr-TR')}</p>
                      <p>Son Tarih: {new Date(task.due_date).toLocaleDateString('tr-TR')}</p>
                      <p>Termin: {new Date(task.deadline_date).toLocaleDateString('tr-TR')}</p>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <p className="text-center text-gray-500 py-8">Bu günde görev yok</p>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setDayTasksDialogOpen(false)}
              className="bg-gray-800 text-white hover:bg-gray-700"
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
